'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { chunk, range } from 'lodash-es';
import { useWallet } from '@solana/wallet-adapter-react';

import { MAX_ALLOWED_BIDS } from '@/lib/constants';
import {
  currentPhaseState,
  isProjectCompleteState,
  isWLPhaseState,
  lastSuccessfulSubmitState,
  selectedProjectDataState,
  transactionsToProcessState,
} from '@/lib/state';
import { tokenToLamports } from '@/lib/utils';

import BidForm from '@/components/BidForm';
import BidsSummary from '@/components/BidsSummary';
import WLBidForm from '@/components/WlBidForm';

export default function BidBox({ token, decimals }) {
  const [bids, setBids] = useState({});
  const [permitted, setPermitted] = useState();
  const [, setTransactionsToProcess] = useRecoilState(transactionsToProcessState);
  const isProjectComplete = useRecoilValue(isProjectCompleteState);
  const isWLPhase = useRecoilValue(isWLPhaseState);
  const lastSuccessfulSubmit = useRecoilValue(lastSuccessfulSubmitState);
  const project = useRecoilValue(selectedProjectDataState);
  const currentPhase = useRecoilValue(currentPhaseState);
  const { publicKey } = useWallet();

  const onAddBid = (bid) => {
    const { qty, amt } = bid;
    if (!(qty && amt)) {
      return;
    }
    setBids((oldBids) => {
      const newBids = { ...oldBids };
      if (!newBids[amt]) {
        newBids[amt] = 0;
      }
      newBids[amt] += Number(qty);
      return newBids;
    });
  };

  const clearAll = useCallback(() => {
    setBids({});
  }, [setBids]);

  const removeBids = useCallback(
    (amtToRemove) => {
      setBids((oldBids) =>
        Object.entries(oldBids).reduce((acc, [amt, qty]) => {
          if (amt !== amtToRemove) {
            acc[amt] = qty;
          }
          return acc;
        }, {})
      );
    },
    [setBids]
  );

  useEffect(() => {
    clearAll();
  }, [lastSuccessfulSubmit, clearAll]);

  const projectId = useMemo(() => {
    return project?.id;
  }, [project]);

  const restrictions = useMemo(() => {
    return JSON.stringify(currentPhase?.restrictions);
  }, [currentPhase]);

  const checkPermitted = useCallback(async () => {
    if (!(projectId && restrictions)) {
      return;
    }
    try {
      const res = await fetch(`/api/phases/${projectId}/permitted`);
      const { permitted } = await res.json();
      setPermitted(permitted);
    } catch (err) {
      console.error(err);
    }
  }, [projectId, restrictions]);

  useEffect(() => {
    checkPermitted();
  }, [checkPermitted]);

  const submitBids = useCallback(
    async (passedBids) => {
      if (!publicKey) {
        toast.error('Wallet not connected');
        return;
      }

      const allBids = Object.entries(passedBids || bids || {})
        .map(([amt, qty]) => {
          const lamports = tokenToLamports(decimals, amt);
          return range(qty).map((_) => ({
            wallet: publicKey?.toBase58(),
            project: project.id,
            lamports,
          }));
        })
        .flat()
        .sort(({ lamports: a }, { lamports: b }) => b - a);
      const transactions = chunk(allBids, MAX_ALLOWED_BIDS);
      setTransactionsToProcess(transactions);
    },
    [bids, publicKey, decimals, project, setTransactionsToProcess]
  );

  if (isProjectComplete) {
    return (
      <div className="flex items-center justify-center font-medium text-xl w-full p-4 uppercase">
        Mint Complete
      </div>
    );
  }

  if (!project) {
    return <div className="bg-gray-300 animate-pulse w-full h-96" />;
  }

  if (!permitted) {
    return (
      <div className="flex items-center justify-center font-medium text-xl w-full p-4 uppercase">
        Not permitted to bid this phase
      </div>
    );
  }

  if (isWLPhase) {
    if (!publicKey) {
      return (
        <div className="flex items-center justify-center font-medium w-full p-4">
          Connect wallet to place WL bids
        </div>
      );
    }

    return (
      <div className="flex flex-col w-full p-4">
        <WLBidForm project={project} onSubmit={submitBids} />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full p-4 pt-2">
      <BidForm project={project} onAddBid={onAddBid} token={token} decimals={decimals} />

      <div className="divider" />

      <BidsSummary
        bids={bids}
        project={project}
        onRemove={removeBids}
        onClear={clearAll}
        onSubmit={submitBids}
        token={token}
        decimals={decimals}
      />
    </div>
  );
}
